import React, { useContext } from "react";
import Strategies from "features/bots/BotForm/Strategies/Strategies";
import TrailingEntryAndTakeProfit from "features/bots/BotForm/common/TrailingEntryAndTakeProfit";
import { SectionTitle } from "features/bots/BotForm/common/styles";
import TitleWithToggle from "features/bots/BotForm/common/TitleWithToggle";
import { BotType, LanguageNameSpaces } from "@shared/types";
import { OrderTypes } from "@shared/features/trades/common/types";
import { Steps, ToggleField } from "@shared/features/bots/BotForm/common/types";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import RestoreChangesButton from "features/bots/BotForm/common/RestoreChangesButton";
import { useTakeProfits } from "@shared/features/bots/BotForm/common/hooks";
import DefaultOrder from "features/bots/BotForm/common/DefaultOrder";
import {
  DYNAMIC_TAKE_PROFITS_TITLE,
  PRICE_ADJUSTMENT_BASELINE_TITLE,
  TAKE_PROFIT_GRACE_TITLE,
} from "@shared/features/bots/BotForm/common/constants";
import Flex from "common/Boxes/Flex.tsx";
import GracePercent from "features/bots/BotForm/common/GracePercent";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

const TakeProfits = () => {
  const { botType, isChannelConfiguration } = useContext(BotFormContext)!;
  const isSignalsBot = botType === BotType.SIGNAL;
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);

  const {
    showTakeProfitStrategy,
    isToggleSection,
    strategyTitle,
    getDefaultValue,
  } = useTakeProfits();

  const getTakeProfitStrategy = () => {
    if (!showTakeProfitStrategy) return undefined;
    const strategies = <Strategies orderType={OrderTypes.TAKE_PROFIT} />;
    if (isChannelConfiguration || isSignalsBot) return strategies;
    return (
      <TitleWithToggle
        title={t("labels.take_profit_strategy")}
        content={strategies}
        fieldType={ToggleField.TAKE_PROFIT_STRATEGY}
        key={ToggleField.TAKE_PROFIT_STRATEGY}
        size={isSignalsBot ? "default" : "small"}
        getDefaultValue={getDefaultValue}
      />
    );
  };

  const trailingInput = (
    <TrailingEntryAndTakeProfit orderType={OrderTypes.TAKE_PROFIT} />
  );

  const graceSelect = <GracePercent isEntry={false} />;

  let content = (
    <>
      {!isSignalsBot && (
        <DefaultOrder
          orderType={OrderTypes.TAKE_PROFIT}
          style={{ marginBottom: 16 }}
        />
      )}
      {getTakeProfitStrategy()}
      {trailingInput}
      {isSignalsBot && !isChannelConfiguration && (
        <Flex style={{ marginTop: 16, rowGap: 16, flexDirection: "column" }}>
          <TitleWithToggle
            title={t(DYNAMIC_TAKE_PROFITS_TITLE)}
            fieldType={ToggleField.TAKE_PROFIT_DYNAMIC}
            content={
              <DefaultOrder
                orderType={OrderTypes.TAKE_PROFIT}
                hidePercent={true}
                baselineTitle={t(PRICE_ADJUSTMENT_BASELINE_TITLE)}
              />
            }
          />
          <TitleWithToggle
            title={t(TAKE_PROFIT_GRACE_TITLE)}
            fieldType={ToggleField.TAKE_PROFIT_GRACE_PERCENT}
            content={graceSelect}
          />
        </Flex>
      )}
    </>
  );
  const titleComponent = <SectionTitle>{strategyTitle}</SectionTitle>;
  if (isToggleSection) {
    content = (
      <TitleWithToggle
        title={titleComponent}
        content={content}
        fieldType={ToggleField.TAKE_PROFITS}
        size="default"
        hideWithOpacity={true}
      />
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <RestoreChangesButton
        tab={Steps.TAKE_PROFITS}
        title={!isToggleSection && titleComponent}
      />
      {content}
    </div>
  );
};

export default React.memo(TakeProfits);
